import type { AttentionItem, AttentionKind, AttentionSeverity } from '@@/api/attention/attention-items';

/**
 * Rendering for the attention card.
 *
 * Kept out of the API response for the same reason notification payloads are
 * structured rather than pre-rendered: the same item has to become a card row
 * now and a digest email later, and a link frozen server-side cannot be rebuilt
 * when a route changes.
 */

export type AttentionDescriptor = {
  title: (item: DescribableItem) => string;
  detail: (item: DescribableItem) => string;
  /** What the primary button says. The verb is the point of the row. */
  action: string;
  href: (item: DescribableItem) => string;
};

const num = (data: Record<string, unknown>, key: string): number | null => {
  const value = data[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const str = (data: Record<string, unknown>, key: string): string => {
  const value = data[key];
  return typeof value === 'string' ? value : '';
};

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

const statistics = (item: DescribableItem) => `/experiments/${item.experimentId}?screen=statistics`;
const details = (item: DescribableItem) => `/experiments/${item.experimentId}`;

const signedPercent = (value: number | null) => (value === null ? '' : `${value > 0 ? '+' : ''}${value.toFixed(2)}%`);

export const ATTENTION_DESCRIPTORS: Record<AttentionKind, AttentionDescriptor> = {
  interaction: {
    title: (item) => `${item.experimentName} is interfering with another experiment`,
    detail: (item) => {
      const other = str(item.data, 'otherExperimentName');
      const reason = str(item.data, 'reason');
      const detail = (item.data.detail as unknown[] | undefined) ?? [];
      const named = detail.filter((entry): entry is string => typeof entry === 'string').slice(0, 3);
      const tail = named.length ? ` (${named.join(', ')})` : '';
      // The reason already names both experiments and what they collide over, so it is
      // used verbatim rather than reworded — the engine and the board editor show the
      // same sentence, and a third phrasing here would be a third thing to keep in step.
      return `${reason}${tail} Running alongside ${other || 'another experiment'} makes both readings unreliable until one of them is stopped.`;
    },
    action: 'Investigate',
    href: statistics,
  },
  srm: {
    title: (item) => `${item.experimentName} did not split as configured`,
    detail: (item) => {
      const p = num(item.data, 'pValue');
      const tail = p === null ? '' : ` (p = ${p < 0.0001 ? '<0.0001' : p.toFixed(4)})`;
      return `The observed traffic split is further from the configured weights than chance explains${tail}. Treat every result on this experiment as suspect until the cause is found.`;
    },
    action: 'Investigate',
    href: statistics,
  },
  'no-data': {
    title: (item) =>
      item.data.neverRecorded === true
        ? `${item.experimentName} has not had a single visitor`
        : `${item.experimentName} has stopped receiving traffic`,
    detail: (item) => {
      const hours = num(item.data, 'windowHours');
      if (item.data.neverRecorded === true) {
        return `Running for over ${hours ?? 24} hours without recording one session. Usually the snippet is not on the page yet, or the URL targeting matches no page that gets visitors.`;
      }
      return `Nothing recorded in the last ${hours ?? 24} hours while the experiment is still running. Usually a targeting rule that stopped matching, or the snippet missing after a deploy.`;
    },
    action: 'Check setup',
    href: details,
  },
  overdue: {
    title: (item) => `${item.experimentName} is past its end date`,
    detail: (item) => {
      const days = num(item.data, 'daysOverdue');
      return `Scheduled to finish ${days === null ? 'earlier' : plural(days, 'day') + ' ago'} but still running and still splitting traffic. End it or extend the schedule.`;
    },
    action: 'Review',
    href: statistics,
  },
  'significant-positive': {
    title: (item) => `${item.experimentName} has a winner`,
    detail: (item) => {
      const lift = signedPercent(num(item.data, 'improvement'));
      const variant = str(item.data, 'variantName');
      return `${variant}${lift ? ` at ${lift}` : ''} is ahead by more than chance explains. Call it and roll out, or keep running for a tighter interval.`;
    },
    action: 'See result',
    href: statistics,
  },
  'significant-negative': {
    title: (item) => `${item.experimentName} is losing`,
    detail: (item) => {
      const lift = signedPercent(num(item.data, 'improvement'));
      const variant = str(item.data, 'variantName');
      return `${variant}${lift ? ` at ${lift}` : ''} is behind the control by more than chance explains. Every day it runs costs conversions.`;
    },
    action: 'See result',
    href: statistics,
  },
  'no-effect': {
    title: (item) => `${item.experimentName} is flat`,
    detail: () =>
      'Sequential testing has ruled out the effect this test was sized to find. Stopping frees the traffic for the next idea.',
    action: 'See result',
    href: statistics,
  },
  'needs-implementation': {
    title: (item) => `${item.experimentName} won but was never built`,
    detail: (item) => {
      const variant = str(item.data, 'variantName');
      return `${variant || 'The winning variant'} is not live and nobody has marked it implemented. A winner that never ships is worth nothing.`;
    },
    action: 'Open queue',
    href: () => '/experiments/implementation',
  },
  'deployed-not-implemented': {
    title: (item) => `${item.experimentName} is still served by Pertento`,
    detail: (item) => {
      const days = num(item.data, 'days');
      return `Rolled out ${days === null ? '' : plural(days, 'day') + ' ago '}and still not permanently coded. It works, but the change belongs in the codebase.`;
    },
    action: 'Open queue',
    href: () => '/experiments/implementation',
  },
  'needs-report': {
    title: (item) => `Write the debrief for ${item.experimentName}`,
    detail: () =>
      'What happened and why is easiest to write while it is fresh. It takes about a minute, and every question is optional.',
    action: 'Write debrief',
    // The tab's value stays `report`, as `info` did when that tab became Brief, so links keep working.
    href: (item) => `/experiments/${item.experimentId}?screen=report`,
  },
  'next-by-pxl': {
    title: (item) => `Next up: ${item.experimentName}`,
    detail: (item) => {
      const total = num(item.data, 'total');
      return `Your highest-scoring draft${total === null ? '' : ` at ${total}/15 on PXL`}. Nothing above needs you right now.`;
    },
    action: 'Open draft',
    href: details,
  },
};

export const SEVERITY_META: Record<AttentionSeverity, { label: string; description: string }> = {
  broken: {
    label: 'Broken',
    description: 'Something is wrong with the experiment itself. Do not act on these results.',
  },
  decide: {
    label: 'Needs a decision',
    description: 'These have reached a conclusion and are waiting on a call.',
  },
  queue: {
    label: 'Queue',
    description: 'Work that is ready to pick up.',
  },
};

export const SEVERITY_ORDER: AttentionSeverity[] = ['broken', 'decide', 'queue'];

/**
 * Anything carrying enough to render a row.
 *
 * Loosened from AttentionItem because Health serves the same items over the
 * wire with `kind` as a plain string: JSON has no union types, and casting at
 * the call site would hide a genuinely unknown kind rather than handle it.
 */
export type DescribableItem = {
  kind: string;
  experimentId: number;
  experimentName: string;
  data: Record<string, unknown>;
};

/**
 * A kind the client does not recognise, which happens for exactly one release
 * whenever the server learns a new signal before the tab is reloaded. Naming
 * the experiment and linking to it is still useful; inventing a sentence for it
 * would not be.
 */
const UNKNOWN_DESCRIPTOR: AttentionDescriptor = {
  title: (item) => item.experimentName,
  detail: () => 'This experiment needs attention.',
  action: 'Open',
  href: details,
};

export const describeAttention = (item: DescribableItem): AttentionDescriptor =>
  ATTENTION_DESCRIPTORS[item.kind as AttentionKind] ?? UNKNOWN_DESCRIPTOR;

/** Groups in display order, dropping any that are empty. */
export function groupBySeverity(items: readonly AttentionItem[]) {
  return SEVERITY_ORDER.map((severity) => ({
    severity,
    meta: SEVERITY_META[severity],
    items: items.filter((item) => item.severity === severity),
  })).filter((group) => group.items.length > 0);
}
